import { useState, useEffect, useRef } from 'react'
import { ChatMessage } from '@/components/portfolio-manager/ChatMessage'
import { TradePreviewCard } from '@/components/portfolio-manager/TradePreviewCard'
import { ContextTogglePanel } from '@/components/portfolio-manager/ContextTogglePanel'
import { ConstitutionEditSuggestionCard } from '@/components/portfolio-manager/ConstitutionEditSuggestion'
import { useAppStore } from '@/store/appStore'
import { invokeEdgeFunction } from '@/lib/edgeFunctions'
import { supabase } from '@/lib/supabase'
import type { ContextToggles, ConstitutionEditSuggestion } from '@/types'

const SUPABASE_URL = import.meta.env.VITE_SUPABASE_URL || ''
const SUPABASE_ANON_KEY = import.meta.env.VITE_SUPABASE_ANON_KEY || ''
const EDGE_CHAT_URL = `${SUPABASE_URL}/functions/v1/ai-chat`

export interface ChatMessageData {
  role: 'user' | 'assistant'
  content: string
  timestamp: string
  suggestions?: string[]
  dataWarnings?: string[]
  tradePreview?: TradePreviewData | null
  constitutionEditSuggestion?: ConstitutionEditSuggestion | null
  contextLoaded?: string[]
  contextErrors?: string[]
  tokenUsage?: {
    tokensUsed: number
    latencyMs: number
    estimatedInputTokens: number
    requestsThisMinute: number
    requestsToday: number
    tokensThisMinute: number
    limits: { rpm: number; tpm: number; rpd: number; maxOutput: number }
  }
}

export interface TradePreviewData {
  ticker: string
  strategy: string
  strike?: number
  expiration?: string
  premium?: number
}

const CONTEXT_TOGGLES_KEY = 'tradingApp_contextToggles'

const DEFAULT_TOGGLES: ContextToggles = {
  portfolio: false,
  constitution: false,
  tmfRankings: false,
  watchlist: false,
  rulesScoring: false,
}

function loadTogglesFromStorage(): ContextToggles {
  try {
    const saved = localStorage.getItem(CONTEXT_TOGGLES_KEY)
    if (saved) {
      const parsed = JSON.parse(saved)
      if (
        typeof parsed === 'object' &&
        parsed !== null &&
        typeof parsed.portfolio === 'boolean' &&
        typeof parsed.constitution === 'boolean' &&
        typeof parsed.tmfRankings === 'boolean' &&
        typeof parsed.watchlist === 'boolean' &&
        typeof parsed.rulesScoring === 'boolean'
      ) {
        return parsed as ContextToggles
      }
    }
  } catch {
    // localStorage unavailable or corrupted — use defaults
  }
  return DEFAULT_TOGGLES
}

function saveTogglesToStorage(toggles: ContextToggles): void {
  try {
    localStorage.setItem(CONTEXT_TOGGLES_KEY, JSON.stringify(toggles))
  } catch {
    // localStorage unavailable — silently ignore
  }
}

function extractTradePreview(content: string): TradePreviewData | null {
  // Look for structured trade suggestion patterns in the AI response
  const tickerMatch = content.match(/\b(ticker|symbol):\s*([A-Z]{1,5})\b/i)
  const strategyMatch = content.match(/\b(strategy|action):\s*(CSP|CC|Covered Call|LEAP Call|LEAP Put|PMCC|Put Credit Spread|Call Credit Spread|Iron Condor|Sell Put|Buy Call)\b/i)
  const strikeMatch = content.match(/\b(strike):\s*\$?(\d+(?:\.\d+)?)\b/i)
  const expMatch = content.match(/\b(exp(?:iration)?|expiry):\s*(\d{4}-\d{2}-\d{2}|\d{1,2}\/\d{1,2}\/\d{2,4})\b/i)
  const premiumMatch = content.match(/\b(premium|credit):\s*\$?(\d+(?:\.\d+)?)\b/i)

  if (tickerMatch && strategyMatch) {
    return {
      ticker: tickerMatch[2].toUpperCase(),
      strategy: strategyMatch[2],
      strike: strikeMatch ? parseFloat(strikeMatch[2]) : undefined,
      expiration: expMatch ? expMatch[2] : undefined,
      premium: premiumMatch ? parseFloat(premiumMatch[2]) : undefined,
    }
  }
  return null
}

export default function ChatbotPage() {
  const [messages, setMessages] = useState<ChatMessageData[]>(() => {
    try {
      const saved = sessionStorage.getItem('pm_chat_history')
      if (!saved) return []
      const parsed = JSON.parse(saved) as ChatMessageData[]
      // Sanitize any existing broken messages (raw JSON from Claude parsing bug)
      return parsed.map(msg => {
        if (msg.role === 'assistant' && msg.content) {
          let content = msg.content
          // Fix: preamble + ```json code block
          if (content.includes('```json') || content.includes('```\n{')) {
            const codeStart = content.indexOf('```json') >= 0 ? content.indexOf('```json') : content.indexOf('```\n{')
            if (codeStart >= 0) {
              const after = content.slice(codeStart)
              const lines = after.split('\n')
              const stripped = lines.slice(1)
              if (stripped.length && stripped[stripped.length - 1].trim() === '```') stripped.pop()
              const inner = stripped.join('\n').trim()
              try {
                const parsed = JSON.parse(inner)
                if (parsed && typeof parsed.response === 'string') {
                  return { ...msg, content: parsed.response }
                }
              } catch { /* fall through */ }
            }
          }
          // Fix: raw JSON string like {"response": "..."}  (may be prettified with newlines)
          const trimmed = content.trim()
          if (trimmed.startsWith('{') && trimmed.includes('"response"')) {
            try {
              const parsed = JSON.parse(trimmed)
              if (parsed && typeof parsed.response === 'string') {
                return { ...msg, content: parsed.response }
              }
            } catch {
              // Try regex extraction for truncated JSON
              const match = trimmed.match(/^\s*\{\s*"response"\s*:\s*"/)
              if (match) {
                const start = match[0].length
                let rest = trimmed.slice(start)
                const endIdx = rest.indexOf('", "suggestions"')
                if (endIdx > 0) rest = rest.slice(0, endIdx)
                return { ...msg, content: rest.replace(/\\n/g, '\n').replace(/\\"/g, '"').replace(/\\\\/g, '\\') }
              }
            }
          }
        }
        return msg
      })
    } catch {
      return []
    }
  })
  const [input, setInput] = useState('')
  const [loading, setLoading] = useState(false)
  const abortControllerRef = useRef<AbortController | null>(null)
  const isSendingRef = useRef(false)
  const [sendCooldown, setSendCooldown] = useState(false)
  const [selectedAccounts, setSelectedAccounts] = useState<string[]>(() => {
    try {
      const saved = localStorage.getItem('tradingApp_chatAccounts')
      return saved ? JSON.parse(saved) : []
    } catch { return [] }
  })
  const [contextToggles, setContextToggles] = useState<ContextToggles>(loadTogglesFromStorage)
  const [constitutionTokenCount, setConstitutionTokenCount] = useState<number | undefined>(undefined)
  const [selectedModel, setSelectedModel] = useState<string>(() => {
    try { return localStorage.getItem('tradingApp_chatModel') || 'auto' } catch { return 'auto' }
  })
  const [noContext, setNoContext] = useState(false)
  const [savedPrompts, setSavedPrompts] = useState<Array<{ id: string; title: string; content: string }>>([])
  const [availableReports, setAvailableReports] = useState<Array<{ id: string; title: string }>>([])
  const [selectedReports, setSelectedReports] = useState<string[]>([])
  const [constitutionSections, setConstitutionSections] = useState<Array<{ heading: string; tokens_est: number }>>([])
  const [selectedConstitutionSections, setSelectedConstitutionSections] = useState<string[]>(() => {
    try {
      const saved = localStorage.getItem('tradingApp_chatConstitutionSections')
      return saved ? JSON.parse(saved) : []
    } catch { return [] }
  })
  const [permanentConstitutionSections, setPermanentConstitutionSections] = useState<string[]>(() => {
    try {
      const saved = localStorage.getItem('tradingApp_chatConstitutionPermanent')
      return saved ? JSON.parse(saved) : []
    } catch { return [] }
  })
  const [chatTimePeriod, setChatTimePeriod] = useState<string>(() => {
    try { return localStorage.getItem('tradingApp_chatTimePeriod') || 'all' } catch { return 'all' }
  })
  const [chatTradeStatus, setChatTradeStatus] = useState<string>(() => {
    try { return localStorage.getItem('tradingApp_chatTradeStatus') || 'all' } catch { return 'all' }
  })
  const [row1Collapsed, setRow1Collapsed] = useState(false)
  const [row2Collapsed, setRow2Collapsed] = useState(false)
  const [inputExpanded, setInputExpanded] = useState(false)
  const [previewOpen, setPreviewOpen] = useState(false)
  const [previewData, setPreviewData] = useState<{ openTrades: any[]; closedTrades: any[]; positions: any[]; totalTradesCount: number } | null>(null)
  const [previewLoading, setPreviewLoading] = useState(false)
  const [tradeCountBadge, setTradeCountBadge] = useState<string>('')
  const messagesEndRef = useRef<HTMLDivElement>(null)

  // Persist conversation to sessionStorage
  useEffect(() => {
    sessionStorage.setItem('pm_chat_history', JSON.stringify(messages))
  }, [messages])

  // Auto-scroll to bottom
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' })
  }, [messages])

  // Fetch saved prompts for the selector
  useEffect(() => {
    // Fetch prompts from Supabase
    supabase.from('prompt_library').select('*').eq('is_active', true).order('display_order')
      .then(({ data }) => setSavedPrompts(data || []))
      .catch(() => {})
    // Fetch saved reports from Supabase
    supabase.from('saved_reports').select('id, title, category, source, created_at').order('created_at', { ascending: false })
      .then(({ data }) => setAvailableReports(data || []))
      .catch(() => {})
    // Fetch constitution sections from Supabase
    supabase.from('constitution').select('content').limit(1)
      .then(({ data }) => {
        if (data && data[0]?.content) {
          const headings = (data[0].content as string)
            .split('\n')
            .filter((l: string) => l.startsWith('## '))
            .map((l: string) => l.replace(/^##\s*/, '').trim())
          setConstitutionSections(headings)
        }
      })
      .catch(() => {})
  }, [])

  const handleToggleChange = (newToggles: ContextToggles) => {
    setContextToggles(newToggles)
    saveTogglesToStorage(newToggles)
  }

  // Auto-enable Portfolio toggle when accounts are selected
  useEffect(() => {
    if (selectedAccounts.length > 0 && !contextToggles.portfolio) {
      const updated = { ...contextToggles, portfolio: true }
      setContextToggles(updated)
      saveTogglesToStorage(updated)
    }
  }, [selectedAccounts])

  // Auto-enable Constitution toggle when constitution sections are selected
  useEffect(() => {
    if (selectedConstitutionSections.length > 0 && !contextToggles.constitution) {
      const updated = { ...contextToggles, constitution: true }
      setContextToggles(updated)
      saveTogglesToStorage(updated)
    }
  }, [selectedConstitutionSections])

  // Fetch trade count badge whenever accounts/period/status change (debounced)
  useEffect(() => {
    const timer = setTimeout(async () => {
      try {
        // Query Supabase directly for trade counts (no Flask dependency)
        const accountSlugs = selectedAccounts.length > 0 ? selectedAccounts : undefined
        let query = supabase.from('trades').select('status, close_date, buy_date')
        if (accountSlugs) {
          query = query.in('account_slug', accountSlugs)
        }
        const { data: trades } = await query
        if (!trades) { setTradeCountBadge('0 open, 0 closed'); return }

        // Open trades are ALWAYS included (they represent current state)
        const openTrades = trades.filter((t: any) => t.status === 'open')

        // Apply time period filter ONLY to closed trades (by close_date)
        const { getTimePeriodRange } = await import('@/components/shared/TimePeriodFilter')
        const range = chatTimePeriod !== 'all' ? getTimePeriodRange(chatTimePeriod) : null

        let closedTrades = trades.filter((t: any) => t.status === 'closed')
        if (range) {
          closedTrades = closedTrades.filter((t: any) => {
            const closeDate = t.close_date || null
            if (!closeDate) return false
            return closeDate >= range.start && closeDate <= range.end
          })
        }

        // Apply status filter for display
        let openCount = openTrades.length
        let closedCount = closedTrades.length
        if (chatTradeStatus === 'open') { closedCount = 0 }
        else if (chatTradeStatus === 'closed') { openCount = 0 }

        setTradeCountBadge(`${openCount} open, ${closedCount} closed`)
      } catch {
        setTradeCountBadge('')
      }
    }, 500)
    return () => clearTimeout(timer)
  }, [selectedAccounts, chatTimePeriod, chatTradeStatus])

  const handlePreview = async () => {
    setPreviewLoading(true)
    setPreviewOpen(true)
    try {
      const accountSlugs = selectedAccounts.length > 0 ? selectedAccounts : undefined
      let query = supabase.from('trades').select('*')
      if (accountSlugs) {
        query = query.in('account_slug', accountSlugs)
      }
      const { data: trades } = await query
      if (!trades) { setPreviewData({ openTrades: [], closedTrades: [], positions: [], totalTradesCount: 0 }); return }

      // Open trades are ALWAYS included (current state, not time-bounded)
      const openTrades = trades.filter((t: any) => t.status === 'open')

      // Time period filter ONLY applies to closed trades (by close_date)
      const { getTimePeriodRange } = await import('@/components/shared/TimePeriodFilter')
      const range = chatTimePeriod !== 'all' ? getTimePeriodRange(chatTimePeriod) : null

      let closedTrades = trades.filter((t: any) => t.status === 'closed')
      if (range) {
        closedTrades = closedTrades.filter((t: any) => {
          const closeDate = t.close_date || null
          if (!closeDate) return false
          return closeDate >= range.start && closeDate <= range.end
        })
      }

      // Apply status filter for display
      let displayOpen = chatTradeStatus === 'closed' ? [] : openTrades
      let displayClosed = chatTradeStatus === 'open' ? [] : closedTrades

      // Fetch positions for selected accounts
      let posQuery = supabase.from('positions').select('*').eq('status', 'open')
      if (accountSlugs) posQuery = posQuery.in('account_slug', accountSlugs)
      const { data: positions } = await posQuery

      setPreviewData({
        openTrades: displayOpen,
        closedTrades: displayClosed,
        positions: positions || [],
        totalTradesCount: displayOpen.length + displayClosed.length,
      })
    } catch { /* silent */ }
    setPreviewLoading(false)
  }

  // Price freshness state from store
  const priceLastRefreshedAt = useAppStore(s => s.priceLastRefreshedAt)
  const isPriceCacheFresh = useAppStore(s => s.isPriceCacheFresh)
  const getPriceCacheAgeMinutes = useAppStore(s => s.getPriceCacheAgeMinutes)
  const [freshnessError, setFreshnessError] = useState<string | null>(null)

  const handleSend = async () => {
    const trimmed = input.trim()
    if (!trimmed || loading || sendCooldown) return

    // === FRESHNESS GATE: Block if prices are stale (>6h) or never fetched ===
    if (!priceLastRefreshedAt) {
      setFreshnessError('No price data loaded. Visit the Watchlist page and click "Refresh Prices" first.')
      isSendingRef.current = false
      return
    }
    if (!isPriceCacheFresh()) {
      const ageMinutes = getPriceCacheAgeMinutes()
      const ageLabel = ageMinutes >= 60 ? `${Math.round(ageMinutes / 60)} hours` : `${ageMinutes} minutes`
      setFreshnessError(`Price data is stale (last updated ${ageLabel} ago). Please refresh prices on the Watchlist page before sending.`)
      isSendingRef.current = false
      return
    }
    setFreshnessError(null)

    // Duplicate request guard — prevent concurrent sends
    if (isSendingRef.current) return
    isSendingRef.current = true

    // === LIVE-FETCH: Option chain data for mentioned tickers (up to 20) ===
    let optionChainData: Record<string, unknown> | undefined
    const OPTION_KEYWORDS = /\b(option|chain|LEAP|expir|strike|put|call|delta|premium|CSP|CC|wheel|spread|iron condor|straddle|strangle)\b/i
    const isOptionQuery = OPTION_KEYWORDS.test(trimmed)

    if (isOptionQuery) {
      // Extract tickers from message (1-5 uppercase letters, excluding common English words)
      const EXCLUDE_WORDS = new Set(['I', 'A', 'THE', 'AND', 'OR', 'FOR', 'IS', 'IT', 'MY', 'TO', 'IN', 'ON', 'AT', 'IF', 'DO', 'ALL', 'CSP', 'CC', 'LEAP', 'CALL', 'PUT', 'OTM', 'ITM', 'ATM', 'IV', 'DTE', 'ETF', 'SMA', 'RSI', 'MACD', 'BE', 'SO', 'AS', 'OF', 'NO', 'VS', 'AM', 'PM', 'UP', 'HAS', 'CAN', 'NOT', 'BUY', 'GET', 'NEW', 'TOP', 'MAX', 'MIN', 'AVG', 'USD'])
      const mentionedTickers = (trimmed.match(/\b[A-Z]{1,5}\b/g) || [])
        .filter(t => !EXCLUDE_WORDS.has(t))
        .slice(0, 20) // Max 20 tickers

      if (mentionedTickers.length > 0) {
        const OPTION_CACHE_KEY = 'tradingApp_optionChainCache'
        const SIX_HOURS_MS = 6 * 60 * 60 * 1000
        let cached: Record<string, { data: unknown; fetchedAt: string }> = {}
        try { cached = JSON.parse(localStorage.getItem(OPTION_CACHE_KEY) || '{}') } catch { /* ignore */ }

        const tickersToFetch: string[] = []
        const freshChains: Record<string, unknown> = {}

        for (const ticker of mentionedTickers) {
          const entry = cached[ticker]
          if (entry && (Date.now() - new Date(entry.fetchedAt).getTime()) < SIX_HOURS_MS) {
            freshChains[ticker] = entry.data
          } else {
            tickersToFetch.push(ticker)
          }
        }

        // Fetch stale/missing tickers
        if (tickersToFetch.length > 0) {
          for (const ticker of tickersToFetch) {
            try {
              const chainData = await invokeEdgeFunction<unknown>('schwab-options', {
                params: { symbol: ticker },
              })
              freshChains[ticker] = chainData
              cached[ticker] = { data: chainData, fetchedAt: new Date().toISOString() }
            } catch (err) {
              console.warn(`[chat] Failed to fetch option chain for ${ticker}:`, err)
            }
          }
          // Persist updated cache to localStorage
          try { localStorage.setItem(OPTION_CACHE_KEY, JSON.stringify(cached)) } catch { /* quota */ }
          // Also persist to Supabase option_chain_cache table (non-blocking)
          for (const ticker of tickersToFetch) {
            if (cached[ticker]) {
              supabase.from('option_chain_cache').upsert({
                ticker,
                chain_data: cached[ticker].data,
                fetched_at: cached[ticker].fetchedAt,
              }, { onConflict: 'ticker' }).then(() => {}).catch(() => {})
            }
          }
        }

        if (Object.keys(freshChains).length > 0) {
          optionChainData = freshChains
        }
      }
    }

    // Fetch selected report contents and append to message
    let messageWithReports = trimmed
    if (selectedReports.length > 0) {
      const reportContents: string[] = []
      for (const reportId of selectedReports) {
        try {
          const { data } = await supabase.from('saved_reports').select('title, content').eq('id', reportId).single()
          if (data) {
            reportContents.push(`\n\n--- Attached Report: ${data.title} ---\n${data.content}`)
          }
        } catch { /* skip failed fetches */ }
      }
      if (reportContents.length > 0) {
        messageWithReports = trimmed + reportContents.join('')
      }
    }

    const userMessage: ChatMessageData = {
      role: 'user',
      content: trimmed,
      timestamp: new Date().toISOString(),
    }
    setMessages(prev => [...prev, userMessage])
    setInput('')
    setLoading(true)

    try {
      const conversationHistory = messages.map(m => ({
        role: m.role,
        content: m.content,
      }))

      const controller = new AbortController()
      abortControllerRef.current = controller

      const res = await fetch(EDGE_CHAT_URL, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${SUPABASE_ANON_KEY}`,
        },
        signal: controller.signal,
        body: JSON.stringify({
          message: messageWithReports,
          ...(noContext ? { noContext: true } : {
            contextToggles,
            accounts: selectedAccounts.length > 0 ? selectedAccounts : undefined,
            constitutionSections: selectedConstitutionSections.length > 0 ? selectedConstitutionSections : undefined,
            tradeStatus: chatTradeStatus !== 'all' ? chatTradeStatus : undefined,
            timePeriod: chatTimePeriod !== 'all' ? chatTimePeriod : undefined,
            ...(optionChainData ? { optionChainData } : {}),
          }),
          selectedModel: selectedModel !== 'auto' ? selectedModel : undefined,
        }),
      })

      if (!res.ok) {
        const errData = await res.json().catch(() => ({}))
        throw new Error(errData.error || `Chat request failed (${res.status})`)
      }

      const data = await res.json()
      const tradePreview = extractTradePreview(data.response || '')

      // Update constitution token count from response metadata
      if (data.constitutionTokenCount != null) {
        setConstitutionTokenCount(data.constitutionTokenCount)
      }

      const assistantMessage: ChatMessageData = {
        role: 'assistant',
        content: data.response || 'No response received.',
        timestamp: data.timestamp || new Date().toISOString(),
        suggestions: data.suggestions || [],
        dataWarnings: data.dataWarnings || [],
        tradePreview,
        constitutionEditSuggestion: data.constitutionEditSuggestion || null,
        contextLoaded: data.contextLoaded || [],
        contextErrors: data.contextErrors || [],
        tokenUsage: data.tokenUsage || (data.tokensUsed != null ? {
          tokensUsed: data.tokensUsed,
          latencyMs: data.latencyMs || 0,
          estimatedInputTokens: 0,
          requestsThisMinute: 0,
          requestsToday: 0,
          tokensThisMinute: data.tokensUsed,
          limits: { rpm: 0, tpm: 0, rpd: 0, maxOutput: 0 },
        } : undefined),
      }
      setMessages(prev => [...prev, assistantMessage])
    } catch (err) {
      if ((err as Error).name === 'AbortError') {
        const stoppedMessage: ChatMessageData = {
          role: 'assistant',
          content: '_Generation stopped by user._',
          timestamp: new Date().toISOString(),
        }
        setMessages(prev => [...prev, stoppedMessage])
      } else {
        const errorMessage: ChatMessageData = {
          role: 'assistant',
          content: `Error: ${(err as Error).message}`,
          timestamp: new Date().toISOString(),
          dataWarnings: ['Failed to communicate with the AI service.'],
        }
        setMessages(prev => [...prev, errorMessage])
      }
    } finally {
      setLoading(false)
      abortControllerRef.current = null
      isSendingRef.current = false
      // 20-second cooldown to respect OpenAI Tier 1 rate limits (3 RPM for GPT-4o)
      setSendCooldown(true)
      setTimeout(() => setSendCooldown(false), 20000)
    }
  }

  const handleStop = () => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort()
      abortControllerRef.current = null
    }
  }

  const handleSuggestionClick = (suggestion: string) => {
    setInput(suggestion)
  }

  const handleClearHistory = () => {
    setMessages([])
    sessionStorage.removeItem('pm_chat_history')
  }

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault()
      handleSend()
    }
  }

  return (
    <div className="flex flex-col h-full">
      {/* Header */}
      <div className="flex items-center justify-between px-6 py-4 border-b border-[var(--color-border)] bg-[var(--color-bg-secondary)]">
        <div className="flex items-center gap-3">
          <span className="text-2xl">💬</span>
          <div>
            <h1 className="text-lg font-bold text-[var(--color-text-primary)]">Portfolio Chat</h1>
            <p className="text-xs text-[var(--color-text-muted)]">AI-powered portfolio assistant</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={handleClearHistory}
            className="px-3 py-1.5 text-xs rounded border border-[var(--color-border)] text-[var(--color-text-muted)] hover:border-red-400 hover:text-red-400 transition-colors"
          >
            🗑️ Clear
          </button>
        </div>
      </div>

      <div className="flex flex-1 overflow-hidden">
        {/* Chat Area */}
        <div className="flex-1 flex flex-col overflow-hidden">
          {/* Messages */}
          <div className="flex-1 overflow-y-auto px-6 py-4 space-y-4">
            {messages.length === 0 && (
              <div className="flex flex-col items-center justify-center h-full text-center">
                <span className="text-5xl mb-4">🤖</span>
                <h2 className="text-lg font-semibold text-[var(--color-text-primary)] mb-2">
                  Portfolio Assistant
                </h2>
                <p className="text-sm text-[var(--color-text-muted)] max-w-md">
                  Ask me about your portfolio, positions, strategies, or get trade ideas.
                  I have access to your accounts, positions, watchlist, and market data.
                </p>
                <div className="mt-6 flex flex-wrap gap-2 justify-center max-w-lg">
                  {[
                    'What are my most profitable positions?',
                    'Show my sector concentration',
                    'Any positions near expiration?',
                    'Suggest a CSP for NVDA',
                  ].map(suggestion => (
                    <button
                      key={suggestion}
                      onClick={() => handleSuggestionClick(suggestion)}
                      className="px-3 py-1.5 text-xs rounded-full border border-[var(--color-border)] text-[var(--color-text-muted)] hover:border-[var(--color-accent)] hover:text-[var(--color-accent)] transition-colors"
                    >
                      {suggestion}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {messages.map((msg, idx) => (
              <div key={idx}>
                <ChatMessage
                  message={msg}
                  onSave={msg.role === 'assistant' ? async () => {
                    const title = prompt('Save as report:', `Report ${new Date().toLocaleDateString()}`)
                    if (!title) return
                    try {
                      await supabase.from('saved_reports').insert({
                        title,
                        content: msg.content,
                        category: 'ai_report',
                        source: 'ai_chat',
                      })
                      alert('Report saved! View it in Report Reader.')
                    } catch { alert('Failed to save') }
                  } : undefined}
                />
                {msg.tradePreview && (
                  <TradePreviewCard trade={msg.tradePreview} />
                )}
                {msg.constitutionEditSuggestion &&
                  msg.constitutionEditSuggestion.targetSection && (
                    <ConstitutionEditSuggestionCard
                      suggestion={msg.constitutionEditSuggestion}
                      onApproved={() => {
                        setMessages(prev =>
                          prev.map((m, i) =>
                            i === idx
                              ? { ...m, constitutionEditSuggestion: null }
                              : m
                          )
                        )
                      }}
                      onRejected={() => {
                        setMessages(prev =>
                          prev.map((m, i) =>
                            i === idx
                              ? { ...m, constitutionEditSuggestion: null }
                              : m
                          )
                        )
                      }}
                    />
                  )}
                {/* Context loaded/errors indicators */}
                {msg.role === 'assistant' && msg.contextLoaded && msg.contextLoaded.length > 0 && (
                  <div className="mt-1 flex flex-wrap items-center gap-1 pl-10">
                    <span className="text-[10px] text-[var(--color-text-muted)]">Context:</span>
                    {msg.contextLoaded.map(ctx => (
                      <span
                        key={ctx}
                        className="px-1.5 py-0.5 text-[10px] rounded bg-green-500/10 text-green-400 border border-green-500/20"
                      >
                        {ctx}
                      </span>
                    ))}
                    {msg.contextErrors && msg.contextErrors.length > 0 &&
                      msg.contextErrors.map(err => (
                        <span
                          key={err}
                          className="px-1.5 py-0.5 text-[10px] rounded bg-red-500/10 text-red-400 border border-red-500/20"
                        >
                          ⚠ {err}
                        </span>
                      ))}
                  </div>
                )}
                {/* Token usage metrics */}
                {msg.role === 'assistant' && msg.tokenUsage && (
                  <div className="mt-1 flex flex-wrap items-center gap-3 pl-10 text-[10px] text-[var(--color-text-muted)]">
                    <span>Tokens: <span className="text-[var(--color-text-primary)]">{msg.tokenUsage.tokensUsed.toLocaleString()}</span></span>
                    <span>Latency: <span className="text-[var(--color-text-primary)]">{(msg.tokenUsage.latencyMs / 1000).toFixed(1)}s</span></span>
                    {msg.tokenUsage.limits.tpm > 0 && (
                      <>
                        <span>TPM: <span className="text-[var(--color-text-primary)]">{msg.tokenUsage.tokensThisMinute.toLocaleString()}</span>/{msg.tokenUsage.limits.tpm.toLocaleString()}</span>
                        <span>RPD: <span className="text-[var(--color-text-primary)]">{msg.tokenUsage.requestsToday}</span>/{msg.tokenUsage.limits.rpd}</span>
                      </>
                    )}
                  </div>
                )}
              </div>
            ))}

            {loading && (
              <div className="flex items-start gap-3">
                <span className="text-lg">🤖</span>
                <div className="bg-[var(--color-bg-tertiary)] rounded-lg px-4 py-3">
                  <div className="flex gap-1">
                    <span className="w-2 h-2 bg-[var(--color-text-muted)] rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
                    <span className="w-2 h-2 bg-[var(--color-text-muted)] rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
                    <span className="w-2 h-2 bg-[var(--color-text-muted)] rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
                  </div>
                </div>
              </div>
            )}

            <div ref={messagesEndRef} />
          </div>

          {/* Last message suggestions */}
          {messages.length > 0 && messages[messages.length - 1].suggestions && messages[messages.length - 1].suggestions!.length > 0 && (
            <div className="px-6 py-2 border-t border-[var(--color-border)] bg-[var(--color-bg-secondary)]">
              <div className="flex flex-wrap gap-2">
                {messages[messages.length - 1].suggestions!.map((s, idx) => (
                  <button
                    key={idx}
                    onClick={() => handleSuggestionClick(s)}
                    className="px-3 py-1 text-xs rounded-full border border-[var(--color-border)] text-[var(--color-text-muted)] hover:border-[var(--color-accent)] hover:text-[var(--color-accent)] transition-colors"
                  >
                    {s}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Context Toggle Panel + Model Selector */}
          <div className="px-6 py-3 border-t border-[var(--color-border)] bg-[var(--color-bg-secondary)] flex items-center gap-3">
            <button
              onClick={() => setNoContext(!noContext)}
              className={`px-2.5 py-1.5 text-xs font-medium rounded-lg border transition-colors flex-shrink-0 ${
                noContext
                  ? 'bg-amber-500 text-white border-amber-500'
                  : 'border-[var(--color-border)] text-[var(--color-text-muted)] hover:bg-[var(--color-bg-tertiary)]'
              }`}
              title="When active, sends your message with zero portfolio/trade context — just a plain question"
            >
              {noContext ? '🚫 No Context' : '📊 Context On'}
            </button>
            {!noContext && (
              <div className="flex-1">
                <ContextTogglePanel
                  toggles={contextToggles}
                  onChange={handleToggleChange}
                  constitutionTokenCount={constitutionTokenCount}
                  selectedAccounts={selectedAccounts}
                />
              </div>
            )}
            {noContext && (
              <span className="flex-1 text-xs text-amber-600">Plain question mode — no portfolio data sent</span>
            )}
            <select
              value={selectedModel}
              onChange={(e) => {
                setSelectedModel(e.target.value)
                try { localStorage.setItem('tradingApp_chatModel', e.target.value) } catch {}
              }}
              className="px-2 py-1.5 text-xs rounded border border-[var(--color-border)] bg-[var(--color-bg-primary)] text-[var(--color-text-primary)] focus:outline-none focus:border-[var(--color-accent)]"
            >
              <option value="auto">Auto (DeepSeek V4 → V3 → Gemini Pro)</option>
              <option value="deepseek-v4">DeepSeek V4 Pro</option>
              <option value="deepseek">DeepSeek V3</option>
              <option value="gemini-pro">Gemini 2.5 Pro</option>
              <option value="claude">Claude Sonnet 4.6</option>
              <option value="openai">GPT-4o</option>
              <option value="openai-mini">GPT-4o-mini</option>
            </select>
          </div>

          {/* Input */}
          <div className="px-6 py-4 border-t border-[var(--color-border)] bg-[var(--color-bg-secondary)]">
            {/* Row 1 (collapsible): Prompt | Accounts | Constitution | Reports */}
            <div className="flex items-center gap-1 mb-1">
              <button onClick={() => setRow1Collapsed(!row1Collapsed)} className="text-[10px] text-[var(--color-text-muted)] hover:text-[var(--color-text-primary)]">
                {row1Collapsed ? '▶' : '▼'} Row 1
              </button>
            </div>
            {!row1Collapsed && (
            <div className="grid grid-cols-3 gap-3 mb-2">
              {/* Column 1: Prompt selector */}
              <div className="flex items-center gap-2">
                <span className="text-[10px] text-[var(--color-text-muted)] shrink-0">Prompt:</span>
                <select
                  onChange={(e) => {
                    const prompt = savedPrompts.find(p => p.id === e.target.value)
                    if (prompt) {
                      const prefix = input.trim() ? input.trim() + '\n\n' : ''
                      setInput(prefix + prompt.content)
                    }
                    e.target.value = ''
                  }}
                  className="flex-1 px-2 py-1.5 text-xs rounded border border-[var(--color-border)] bg-[var(--color-bg-primary)] text-[var(--color-text-primary)] focus:outline-none focus:border-[var(--color-accent)]"
                  defaultValue=""
                >
                  <option value="" disabled>Select a prompt...</option>
                  {savedPrompts.map(p => (
                    <option key={p.id} value={p.id}>{p.title}</option>
                  ))}
                </select>
              </div>

              {/* Column 2: Account multi-select dropdown */}
              <div className="flex items-center gap-2">
                <span className="text-[10px] text-[var(--color-text-muted)] shrink-0">Accounts:</span>
                <div className="relative flex-1">
                  <button
                    onClick={() => {
                      const el = document.getElementById('account-dropdown')
                      if (el) el.classList.toggle('hidden')
                      document.getElementById('report-dropdown')?.classList.add('hidden')
                      document.getElementById('constitution-dropdown')?.classList.add('hidden')
                    }}
                    className="w-full px-2 py-1.5 text-xs rounded border border-[var(--color-border)] bg-[var(--color-bg-primary)] text-[var(--color-text-primary)] text-left focus:outline-none focus:border-[var(--color-accent)]"
                  >
                    {selectedAccounts.length === 0 ? 'All accounts' : `${selectedAccounts.length} selected`}
                    <span className="float-right">▾</span>
                  </button>
                  <div id="account-dropdown" className="hidden absolute z-50 bottom-full mb-1 w-full bg-white border border-gray-200 rounded-lg shadow-lg py-1 max-h-48 overflow-y-auto">
                    <button
                      onClick={() => {
                        setSelectedAccounts([])
                        try { localStorage.setItem('tradingApp_chatAccounts', '[]') } catch {}
                      }}
                      className="w-full px-3 py-1.5 text-xs text-left hover:bg-gray-100 text-blue-600 font-medium"
                    >
                      All accounts (clear selection)
                    </button>
                    {[
                      { slug: 'wheel', label: 'Wheel' },
                      { slug: 'amzn', label: 'AMZN' },
                      { slug: 'leap-calls', label: 'LEAP Calls' },
                      { slug: 'leap-puts', label: 'LEAP Puts' },
                      { slug: 'richas-roth', label: "Richa's Roth" },
                      { slug: 'yogeshs-roth', label: "Yogesh's Roth" },
                      { slug: 'hsa', label: 'HSA' },
                      { slug: 'spy-spread', label: 'SPY Spread' },
                      { slug: 'shares', label: 'Shares' },
                    ].map(a => (
                      <label key={a.slug} className="flex items-center gap-2 px-3 py-1.5 text-xs hover:bg-gray-100 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={selectedAccounts.includes(a.slug)}
                          onChange={() => {
                            const updated = selectedAccounts.includes(a.slug)
                              ? selectedAccounts.filter(s => s !== a.slug)
                              : [...selectedAccounts, a.slug]
                            setSelectedAccounts(updated)
                            try { localStorage.setItem('tradingApp_chatAccounts', JSON.stringify(updated)) } catch {}
                          }}
                          className="rounded border-gray-300"
                        />
                        <span>{a.label}</span>
                      </label>
                    ))}
                  </div>
                </div>
              </div>

              {/* Constitution section selector removed — full doc always sent via cache */}

              {/* Column 3: Report selector (multi-select, upward) */}
              <div className="flex items-center gap-2">
                <span className="text-[10px] text-[var(--color-text-muted)] shrink-0">Reports:</span>
                <div className="relative flex-1">
                  <button
                    onClick={() => {
                      const el = document.getElementById('report-dropdown')
                      if (el) el.classList.toggle('hidden')
                      document.getElementById('account-dropdown')?.classList.add('hidden')
                      document.getElementById('constitution-dropdown')?.classList.add('hidden')
                    }}
                    className="w-full px-2 py-1.5 text-xs rounded border border-[var(--color-border)] bg-[var(--color-bg-primary)] text-[var(--color-text-primary)] text-left focus:outline-none focus:border-[var(--color-accent)]"
                  >
                    {selectedReports.length === 0 ? 'None attached' : `${selectedReports.length} attached`}
                    <span className="float-right">▾</span>
                  </button>
                  <div id="report-dropdown" className="hidden absolute z-50 bottom-full mb-1 w-full bg-white border border-gray-200 rounded-lg shadow-lg py-1 max-h-48 overflow-y-auto">
                    {availableReports.length === 0 ? (
                      <div className="px-3 py-2 text-xs text-gray-400">No saved reports yet</div>
                    ) : (
                      <>
                        <button
                          onClick={() => setSelectedReports([])}
                          className="w-full px-3 py-1.5 text-xs text-left hover:bg-gray-100 text-blue-600 font-medium"
                        >
                          Clear selection
                        </button>
                        {availableReports.map(r => (
                          <label key={r.id} className="flex items-center gap-2 px-3 py-1.5 text-xs hover:bg-gray-100 cursor-pointer">
                            <input
                              type="checkbox"
                              checked={selectedReports.includes(r.id)}
                              onChange={() => {
                                const updated = selectedReports.includes(r.id)
                                  ? selectedReports.filter(id => id !== r.id)
                                  : [...selectedReports, r.id]
                                setSelectedReports(updated)
                              }}
                              className="rounded border-gray-300"
                            />
                            <span className="truncate">{r.title}</span>
                          </label>
                        ))}
                      </>
                    )}
                  </div>
                </div>
              </div>
            </div>
            )}

            {/* Row 2 (collapsible): Time Period | Trade Status | (empty) | (empty) */}
            <div className="flex items-center gap-1 mb-1">
              <button onClick={() => setRow2Collapsed(!row2Collapsed)} className="text-[10px] text-[var(--color-text-muted)] hover:text-[var(--color-text-primary)]">
                {row2Collapsed ? '▶' : '▼'} Row 2
              </button>
            </div>
            {!row2Collapsed && (
            <div className="grid grid-cols-4 gap-3 mb-2">
              {/* Column 1: Time Period */}
              <div className="flex items-center gap-2">
                <span className="text-[10px] text-[var(--color-text-muted)] shrink-0">Period:</span>
                <select
                  value={chatTimePeriod}
                  onChange={(e) => {
                    setChatTimePeriod(e.target.value)
                    try { localStorage.setItem('tradingApp_chatTimePeriod', e.target.value) } catch {}
                  }}
                  className="flex-1 px-2 py-1.5 text-xs rounded border border-[var(--color-border)] bg-[var(--color-bg-primary)] text-[var(--color-text-primary)] focus:outline-none focus:border-[var(--color-accent)]"
                >
                  <option value="all">All Time</option>
                  <option value="this_week">This Week</option>
                  <option value="this_month">This Month</option>
                  <option value="this_quarter">This Quarter</option>
                  <option value="ytd">YTD</option>
                  <option value="last_30">Last 30 Days</option>
                  <option value="last_90">Last 90 Days</option>
                  <option value="2026">2026</option>
                  <option value="2025">2025</option>
                </select>
              </div>

              {/* Column 2: Trade Status Filter */}
              <div className="flex items-center gap-2">
                <span className="text-[10px] text-[var(--color-text-muted)] shrink-0">Trades:</span>
                <select
                  value={chatTradeStatus}
                  onChange={(e) => {
                    setChatTradeStatus(e.target.value)
                    try { localStorage.setItem('tradingApp_chatTradeStatus', e.target.value) } catch {}
                  }}
                  className="flex-1 px-2 py-1.5 text-xs rounded border border-[var(--color-border)] bg-[var(--color-bg-primary)] text-[var(--color-text-primary)] focus:outline-none focus:border-[var(--color-accent)]"
                >
                  <option value="all">Open + Closed</option>
                  <option value="open">Open Only</option>
                  <option value="closed">Closed Only</option>
                </select>
              </div>

              {/* Column 3: Trade count badge */}
              <div className="flex items-center gap-2">
                {tradeCountBadge && (
                  <span className="text-[10px] text-[var(--color-text-muted)]">
                    📊 {tradeCountBadge}
                  </span>
                )}
              </div>
              {/* Column 4: Preview button */}
              <div className="flex items-center">
                <button
                  onClick={handlePreview}
                  className="px-3 py-1.5 text-xs rounded border border-[var(--color-border)] text-[var(--color-text-muted)] hover:text-[var(--color-accent)] hover:border-[var(--color-accent)] transition-colors"
                >
                  👁 Preview Data
                </button>
              </div>
            </div>
            )}

            {/* Freshness indicator + error banner */}
            {freshnessError && (
              <div className="mb-2 px-3 py-2 rounded bg-amber-50 border border-amber-200 text-amber-800 text-xs flex items-center justify-between">
                <span>⚠️ {freshnessError}</span>
                <a href="/" className="ml-2 underline hover:text-amber-900 font-medium">Go to Watchlist</a>
              </div>
            )}
            <div className="flex items-center gap-2 mb-1">
              {priceLastRefreshedAt ? (
                <span className={`text-[10px] px-2 py-0.5 rounded font-medium ${
                  getPriceCacheAgeMinutes() < 120 ? 'bg-green-100 text-green-700' :
                  getPriceCacheAgeMinutes() < 360 ? 'bg-amber-100 text-amber-700' :
                  'bg-red-100 text-red-700'
                }`}>
                  {getPriceCacheAgeMinutes() < 120 ? '🟢' : getPriceCacheAgeMinutes() < 360 ? '🟡' : '🔴'} Prices: {
                    getPriceCacheAgeMinutes() < 60 ? `${getPriceCacheAgeMinutes()}m ago` : `${Math.round(getPriceCacheAgeMinutes() / 60)}h ago`
                  }
                </span>
              ) : (
                <span className="text-[10px] px-2 py-0.5 rounded bg-red-100 text-red-700 font-medium">🔴 No prices loaded</span>
              )}
            </div>

            {/* Textarea with expand/collapse */}
            <div className={`flex gap-3 ${inputExpanded ? 'flex-col' : ''}`}>
              <div className="flex gap-3 flex-1">
                <textarea
                  value={input}
                  onChange={e => setInput(e.target.value)}
                  onKeyDown={handleKeyDown}
                  placeholder="Ask about your portfolio..."
                  rows={inputExpanded ? 12 : 1}
                  className={`flex-1 resize-none px-4 py-2.5 rounded-lg border border-[var(--color-border)] bg-[var(--color-bg-primary)] text-sm text-[var(--color-text-primary)] placeholder-[var(--color-text-muted)] focus:outline-none focus:border-[var(--color-accent)] transition-colors ${inputExpanded ? 'h-[60vh]' : ''}`}
                  disabled={loading}
                />
                <div className="flex flex-col gap-1">
                  <button
                    onClick={() => setInputExpanded(!inputExpanded)}
                    className="px-2 py-1 text-xs rounded border border-[var(--color-border)] text-[var(--color-text-muted)] hover:text-[var(--color-text-primary)] hover:border-[var(--color-accent)]"
                    title={inputExpanded ? 'Collapse input' : 'Expand input'}
                  >
                    {inputExpanded ? '⊟' : '⊞'}
                  </button>
                  <button
                    onClick={loading ? handleStop : handleSend}
                    disabled={!loading && (!input.trim() || sendCooldown)}
                    className={`px-4 py-2.5 rounded-lg text-sm font-medium transition-opacity ${
                      loading
                        ? 'bg-red-500 text-white hover:bg-red-600'
                        : sendCooldown
                        ? 'bg-gray-400 text-white cursor-not-allowed opacity-50'
                        : 'bg-[var(--color-accent)] text-white disabled:opacity-50 disabled:cursor-not-allowed hover:opacity-90'
                    }`}
                  >
                    {loading ? '■ Stop' : sendCooldown ? '⏳ Wait' : 'Send'}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Preview Modal */}
      {previewOpen && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/50" onClick={() => setPreviewOpen(false)}>
          <div className="bg-white rounded-2xl shadow-2xl w-[90vw] max-h-[85vh] flex flex-col" onClick={e => e.stopPropagation()}>
            {/* Modal header */}
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
              <div>
                <h2 className="text-lg font-bold text-gray-900">Data Preview — What GPT Will Receive</h2>
                {previewData && (
                  <p className="text-xs text-gray-500 mt-1">
                    {previewData.openTrades?.length || 0} open trades, {previewData.closedTrades?.length || 0} closed trades, {previewData.positions?.length || 0} positions
                  </p>
                )}
              </div>
              <button onClick={() => setPreviewOpen(false)} className="text-gray-400 hover:text-gray-600 text-xl">✕</button>
            </div>

            {/* Modal body — scrollable */}
            <div className="flex-1 overflow-y-auto px-6 py-4">
              {previewLoading ? (
                <div className="flex items-center justify-center py-12">
                  <span className="text-gray-400">Loading preview...</span>
                </div>
              ) : previewData ? (
                <div className="space-y-6">
                  {/* Open Trades */}
                  {previewData.openTrades && previewData.openTrades.length > 0 && (
                    <div>
                      <h3 className="text-sm font-semibold text-gray-700 mb-2">Open Trades ({previewData.openTrades.length})</h3>
                      <div className="overflow-x-auto">
                        <table className="w-full text-xs border border-gray-200 rounded">
                          <thead className="bg-gray-50">
                            <tr>
                              <th className="px-2 py-1.5 text-left font-medium text-gray-600">Ticker</th>
                              <th className="px-2 py-1.5 text-left font-medium text-gray-600">Action</th>
                              <th className="px-2 py-1.5 text-left font-medium text-gray-600">Strike</th>
                              <th className="px-2 py-1.5 text-left font-medium text-gray-600">Exp Date</th>
                              <th className="px-2 py-1.5 text-left font-medium text-gray-600">Qty</th>
                              <th className="px-2 py-1.5 text-left font-medium text-gray-600">Premium</th>
                              <th className="px-2 py-1.5 text-left font-medium text-gray-600">Total</th>
                              <th className="px-2 py-1.5 text-left font-medium text-gray-600">Delta</th>
                              <th className="px-2 py-1.5 text-left font-medium text-gray-600">Buy Date</th>
                              <th className="px-2 py-1.5 text-left font-medium text-gray-600">Account</th>
                            </tr>
                          </thead>
                          <tbody>
                            {previewData.openTrades.map((t: any, idx: number) => (
                              <tr key={idx} className="border-t border-gray-100 hover:bg-gray-50">
                                <td className="px-2 py-1.5 font-medium">{t.ticker}</td>
                                <td className="px-2 py-1.5">{t.action}</td>
                                <td className="px-2 py-1.5">${t.strikePrice || t.strike_price || t.strike || '—'}</td>
                                <td className="px-2 py-1.5">{t.expDate || t.exp_date || '—'}</td>
                                <td className="px-2 py-1.5">{t.qty || 1}</td>
                                <td className="px-2 py-1.5">${t.premium || '—'}</td>
                                <td className="px-2 py-1.5">${t.total || '—'}</td>
                                <td className="px-2 py-1.5">{t.delta || '—'}</td>
                                <td className="px-2 py-1.5">{t.buyDate || t.buy_date || '—'}</td>
                                <td className="px-2 py-1.5 text-gray-400">{t.account_slug || t.accountSlug || '—'}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  )}

                  {/* Closed Trades */}
                  {previewData.closedTrades && previewData.closedTrades.length > 0 && (
                    <div>
                      <h3 className="text-sm font-semibold text-gray-700 mb-2">Closed Trades ({previewData.closedTrades.length})</h3>
                      <div className="overflow-x-auto">
                        <table className="w-full text-xs border border-gray-200 rounded">
                          <thead className="bg-gray-50">
                            <tr>
                              <th className="px-2 py-1.5 text-left font-medium text-gray-600">Ticker</th>
                              <th className="px-2 py-1.5 text-left font-medium text-gray-600">Action</th>
                              <th className="px-2 py-1.5 text-left font-medium text-gray-600">Strike</th>
                              <th className="px-2 py-1.5 text-left font-medium text-gray-600">Exp Date</th>
                              <th className="px-2 py-1.5 text-left font-medium text-gray-600">Qty</th>
                              <th className="px-2 py-1.5 text-left font-medium text-gray-600">Net P&L</th>
                              <th className="px-2 py-1.5 text-left font-medium text-gray-600">Return %</th>
                              <th className="px-2 py-1.5 text-left font-medium text-gray-600">Close Action</th>
                              <th className="px-2 py-1.5 text-left font-medium text-gray-600">Close Date</th>
                              <th className="px-2 py-1.5 text-left font-medium text-gray-600">Days</th>
                              <th className="px-2 py-1.5 text-left font-medium text-gray-600">Account</th>
                            </tr>
                          </thead>
                          <tbody>
                            {previewData.closedTrades.map((t: any, idx: number) => (
                              <tr key={idx} className="border-t border-gray-100 hover:bg-gray-50">
                                <td className="px-2 py-1.5 font-medium">{t.ticker}</td>
                                <td className="px-2 py-1.5">{t.action}</td>
                                <td className="px-2 py-1.5">${t.strikePrice || t.strike_price || t.strike || '—'}</td>
                                <td className="px-2 py-1.5">{t.expDate || t.exp_date || '—'}</td>
                                <td className="px-2 py-1.5">{t.qty || 1}</td>
                                <td className="px-2 py-1.5">{t.netPosition != null ? `$${Number(t.netPosition).toFixed(2)}` : (t.net_position != null ? `$${Number(t.net_position).toFixed(2)}` : '—')}</td>
                                <td className="px-2 py-1.5">{t.returnPct != null ? `${Number(t.returnPct).toFixed(1)}%` : (t.return_pct != null ? `${Number(t.return_pct).toFixed(1)}%` : '—')}</td>
                                <td className="px-2 py-1.5">{t.closeAction || t.close_action || '—'}</td>
                                <td className="px-2 py-1.5">{t.closeDate || t.close_date || '—'}</td>
                                <td className="px-2 py-1.5">{t.daysKept || t.days_kept || '—'}</td>
                                <td className="px-2 py-1.5 text-gray-400">{t.account_slug || t.accountSlug || '—'}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  )}

                  {/* Positions */}
                  {previewData.positions && previewData.positions.length > 0 && (
                    <div>
                      <h3 className="text-sm font-semibold text-gray-700 mb-2">Positions ({previewData.positions.length})</h3>
                      <div className="overflow-x-auto">
                        <table className="w-full text-xs border border-gray-200 rounded">
                          <thead className="bg-gray-50">
                            <tr>
                              <th className="px-2 py-1.5 text-left font-medium text-gray-600">Ticker</th>
                              <th className="px-2 py-1.5 text-left font-medium text-gray-600">Shares</th>
                              <th className="px-2 py-1.5 text-left font-medium text-gray-600">Cost Basis</th>
                              <th className="px-2 py-1.5 text-left font-medium text-gray-600">Acquired</th>
                              <th className="px-2 py-1.5 text-left font-medium text-gray-600">Account</th>
                            </tr>
                          </thead>
                          <tbody>
                            {previewData.positions.map((p: any, idx: number) => (
                              <tr key={idx} className="border-t border-gray-100 hover:bg-gray-50">
                                <td className="px-2 py-1.5 font-medium">{p.ticker}</td>
                                <td className="px-2 py-1.5">{p.quantity || p.qty || '—'}</td>
                                <td className="px-2 py-1.5">${p.costBasis || p.cost_basis || '—'}</td>
                                <td className="px-2 py-1.5">{p.assignedDate || p.acquisitionDate || '—'}</td>
                                <td className="px-2 py-1.5 text-gray-400">{p.account_slug || p.accountSlug || '—'}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  )}

                  {previewData.totalTradesCount === 0 && (
                    <div className="text-center py-12 text-gray-400">
                      No trades match the current filters. Adjust Time Period or Trade Status.
                    </div>
                  )}
                </div>
              ) : null}
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
